import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Building2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  initialNombre: string;
  initialNit?: string | null;
}

export function OrgSettingsModal({
  open,
  onOpenChange,
  orgId,
  initialNombre,
  initialNit,
}: Props) {
  const queryClient = useQueryClient();
  const { refreshMemberships, broadcastOrgDataChanged } = useAuth();
  const [nombre, setNombre] = useState(initialNombre ?? "");
  const [nit, setNit] = useState(initialNit ?? "");
  const [loadingRow, setLoadingRow] = useState(false);

  useEffect(() => {
    if (!open) return;
    setNombre(initialNombre ?? "");
    setNit(initialNit ?? "");
    // Fetch fresh row (NIT may not be in the membership cache)
    let cancelled = false;
    setLoadingRow(true);
    supabase
      .from("organizaciones")
      .select("nombre, nit")
      .eq("id", orgId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled || !data) return;
        setNombre(data.nombre ?? "");
        setNit((data as any).nit ?? "");
      })
      .then(() => !cancelled && setLoadingRow(false));
    return () => {
      cancelled = true;
    };
  }, [open, orgId, initialNombre, initialNit]);

  const mutation = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc("update_organization_details", {
        _org_id: orgId,
        _nombre: nombre,
        _nit: nit || null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async () => {
      toast.success("Datos de la empresa actualizados");
      await refreshMemberships();
      queryClient.invalidateQueries({ queryKey: ["all-organizations"] });
      queryClient.invalidateQueries({ queryKey: ["all-users-global"] });
      queryClient.invalidateQueries({ queryKey: ["org-quota"] });
      queryClient.invalidateQueries({ queryKey: ["team-members"] });
      broadcastOrgDataChanged();
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err?.message ?? "No se pudo actualizar la organización");
    },
  });

  const trimmed = nombre.trim();
  const disabled = mutation.isPending || loadingRow || trimmed.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 className="h-4 w-4" />
            Ajustes de Empresa
          </DialogTitle>
          <DialogDescription>
            Actualiza la razón social y el NIT de tu organización. Estos datos
            aparecerán en la app y en los documentos generados.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="org-nombre">Razón social *</Label>
            <Input
              id="org-nombre"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              maxLength={200}
              placeholder="Nombre de la empresa"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="org-nit">NIT</Label>
            <Input
              id="org-nit"
              value={nit}
              onChange={(e) => setNit(e.target.value)}
              maxLength={50}
              placeholder="900.123.456-7"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={() => mutation.mutate()} disabled={disabled}>
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Guardar cambios
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
